"use client";

import React from "react";
import { motion } from "framer-motion";
import { Badge } from "../ui/badge";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

type Props = {
  badgeText?: string;
  title: string;
  subtitle?: string;
  titleId?: string;
  titleSize?: "default" | "compact" | "hero";
  titleTag?: "h1" | "h2";
  titleStyle?: "default" | "editorial";
  align?: "left" | "center" | "right";
  action?: React.ReactNode;
  subtitlePosition?: "below" | "aside";
};

const HomeSection = (props: Props) => {
  const { t } = useTranslation();
  const align = props.align ?? "center";
  const Heading = props.titleTag === "h1" ? motion.h1 : motion.h2;
  const isHero = props.titleSize === "hero";
  const textAlignment = {
    left: "mr-auto text-left",
    center: "mx-auto text-center",
    right: "ml-auto text-right",
  }[align];
  const badgeAlignment = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
  }[align];

  const subtitle = props.subtitle ? (
    <motion.p
      className={cn(
        "text-balance font-medium",
        isHero
          ? "max-w-xl text-lg leading-relaxed text-slate-700 sm:text-xl dark:text-blue-100"
          : "max-w-4xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8 md:text-xl dark:text-slate-300",
        textAlignment,
      )}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.95, ease: [0.165, 0.84, 0.44, 1] }}
      viewport={{ once: true }}
    >
      {t(props.subtitle)}
    </motion.p>
  ) : null;

  const heading = (
    <>
      {props.badgeText && (
        <div className={cn("flex", badgeAlignment)}>
          <Badge
            variant="outline"
            className="rounded-full border border-white/65 bg-white/65 px-3.5 py-1 text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm dark:border-white/15 dark:bg-white/10 dark:text-slate-200"
          >
            {t(props.badgeText)}
          </Badge>
        </div>
      )}
      <Heading
        id={props.titleId}
        className={cn(
          isHero
            ? "mb-6 mt-5 max-w-[15ch] text-[clamp(2.5rem,6vw,5.5rem)] font-bold text-slate-900 dark:text-white"
            : "bg-gradient-to-r from-slate-900 via-indigo-600 to-slate-900 bg-clip-text py-6 font-semibold text-transparent sm:py-8 lg:max-w-[16ch] dark:from-white dark:via-sky-200 dark:to-white",
          !isHero && (props.titleSize === "compact"
            ? "text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl"
            : "text-4xl sm:text-4xl md:text-6xl lg:text-7xl"),
          props.titleStyle === "editorial"
            ? "text-balance normal-case leading-[1.02] tracking-[-0.055em]"
            : "uppercase tracking-tight",
          textAlignment,
        )}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.95, ease: [0.165, 0.84, 0.44, 1] }}
        viewport={{ once: true }}
      >
        {t(props.title)}
      </Heading>
      {props.subtitlePosition !== "aside" && subtitle}
    </>
  );

  if (!props.action && props.subtitlePosition !== "aside") return heading;

  return (
    <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
      <div className="min-w-0 flex-1">{heading}</div>
      <div className="w-full space-y-7 lg:max-w-sm lg:shrink-0 lg:pb-1">
        {props.subtitlePosition === "aside" && subtitle}
        {props.action}
      </div>
    </div>
  );
};

export default HomeSection;
